import { arrayEquals } from "@ciq/shared/utils/array"
import { ZoneType } from "@ciq/shared/utils/const"

// Common filter for hiding unselected items
export const NOT_SELECTED_FILTER = ["==", "id", "!selected"]

// Filter that always shows all features in the source
export const ALWAYS_SHOW_FILTER = ["has", "id"]

/**
 * Base class to manage a single map layer
 */
export class BaseLayer {
  constructor(layerId, layerType, sourceId, paintProperties, defaultFilter = null) {
    this.layerId = layerId
    this.layerType = layerType
    this.sourceId = sourceId
    this.paintProperties = paintProperties
    this.defaultFilter = defaultFilter || NOT_SELECTED_FILTER
  }

  /**
   * Add layer to the map
   */
  addToMap(map, insertAboveLayerId, filter = null) {
    if (!map || map.getLayer(this.layerId)) {
      return
    }

    const args = {}
    if (filter) {
      args.filter = filter
    } else if (this.defaultFilter) {
      args.filter = this.defaultFilter
    }

    map.addLayer(
      {
        id: this.layerId,
        type: this.layerType,
        source: this.sourceId,
        paint: this.paintProperties,
        ...args,
      },
      insertAboveLayerId
    )
  }

  /**
   * Update visibility for the layer
   */
  setVisibility(map, visible) {
    if (!map) return

    const visibility = visible ? "visible" : "none"

    if (
      map.getLayer(this.layerId) &&
      map.getLayoutProperty(this.layerId, "visibility") !== visibility
    ) {
      map.setLayoutProperty(this.layerId, "visibility", visibility)
    }
  }

  /**
   * Update filter for the layer
   */
  setFilter(map, filter) {
    if (!map || !map.getLayer(this.layerId)) return

    let layerFilter = filter
    if (layerFilter == null && this.defaultFilter) {
      layerFilter = this.defaultFilter
    }

    map.setFilter(this.layerId, layerFilter)
  }

  /**
   * Update paint property for the layer
   */
  setPaintProperty(map, property, value) {
    if (!map || !map.getLayer(this.layerId)) return

    const current = map.getPaintProperty(this.layerId, property)
    const shouldUpdate =
      Array.isArray(current) && Array.isArray(value)
        ? !arrayEquals(current, value)
        : current !== value

    if (shouldUpdate) {
      map.setPaintProperty(this.layerId, property, value)
    }
  }
}

// Helper to get first symbol layer for insertion
export const getFirstSymbolLayerId = map => {
  const layers = map.getStyle().layers
  for (const layer of layers) {
    if (layer.type === "symbol") {
      return layer.id
    }
  }

  return null
}

/**
 * Hide all symbol (label) layers on the map.
 * Used to suppress base map text labels (street names, POI names, etc.)
 */
export const hideAllSymbolLayers = map => {
  if (!map) return

  const style = map.getStyle()
  if (!style?.layers) return

  for (const layer of style.layers) {
    if (layer.type === "symbol") {
      map.setLayoutProperty(layer.id, "visibility", "none")
    }
  }
}

// Common resource types to skip
export const SKIP_RESOURCE_TYPES = [ZoneType.SCHOOL, ZoneType.CAMPUS]
